import moment from 'moment'
import * as R from 'ramda'

import type {
  Client,
  Payment as GraphQlPayment,
  RetailOrder,
  RetailPaymentDetail,
  Totals,
} from '~/api/graphql/generated/types'
import type {
  BatchInvoice,
  InvoiceOrEstimate,
  InvoiceTotals,
  Payment,
} from '~/types'

export interface InvoiceStateFlags {
  hasChewyPayments: boolean
  hasDeposit: boolean
  isBatchInvoice: boolean
  isChewyCheckoutEnabledAndHasRetailOrder: boolean
  showClientBalance: boolean
  showMissingShippingAddressWarning: boolean
  showServiceFeeOptions: boolean
  showTotalPaidLabel: boolean
}

export interface GetInvoiceStateFlagsParams {
  client: Client | undefined
  completedPaymentsLength: number
  invoice: InvoiceOrEstimate | BatchInvoice
  isBatchInvoice: boolean
  isChewyCheckoutEnabled: boolean
  isEstimate: boolean
  isMovedToChewyCart: boolean
  minimumPaymentsToCollapse: number
  retailOrder: RetailOrder | undefined
}

export const getInvoiceStateFlags = ({
  invoice,
  isEstimate,
  isChewyCheckoutEnabled,
  isMovedToChewyCart,
  retailOrder,
  client,
  completedPaymentsLength,
  isBatchInvoice,
  minimumPaymentsToCollapse,
}: GetInvoiceStateFlagsParams): InvoiceStateFlags => {
  const isChewyCheckoutEnabledAndHasRetailOrder =
    isChewyCheckoutEnabled && Boolean(retailOrder) && !isMovedToChewyCart

  const chewyPayments = retailOrder?.paymentDetails || []
  const hasChewyPayments =
    isChewyCheckoutEnabledAndHasRetailOrder && !R.isEmpty(chewyPayments)

  const hasServiceFee = Boolean(invoice.serviceFee)
  const showServiceFeeOptions = invoice.dueToPayNoFee !== 0 && hasServiceFee

  const showMissingShippingAddressWarning =
    ((client as any)?.shippingAddresses || []).length <= 0

  const showClientBalance = invoice.paid
    ? Boolean(invoice.paidDate && invoice.balanceAfterPaid)
    : true

  const showTotalPaidLabel =
    completedPaymentsLength >= minimumPaymentsToCollapse

  const hasDeposit = isEstimate && !R.isNil(invoice.requiredDeposit)

  return {
    isChewyCheckoutEnabledAndHasRetailOrder,
    hasDeposit,
    hasChewyPayments,
    showServiceFeeOptions,
    showMissingShippingAddressWarning,
    showClientBalance,
    showTotalPaidLabel,
    isBatchInvoice,
  }
}

export const calculateDeposit = (
  includeServiceFee: boolean,
  totals: InvoiceTotals,
) =>
  includeServiceFee ? totals.requiredDeposit : totals.requiredDepositWithoutFee

export const calculateTotalAmount = (
  isEstimate: boolean,
  includeServiceFee: boolean,
  invoice: InvoiceOrEstimate | BatchInvoice,
  totals: InvoiceTotals,
) => {
  const totalAmountNoFee = isEstimate
    ? R.prop('totalWithoutFee', totals)
    : R.prop('dueToPayNoFee', invoice)

  const totalAmountFee = isEstimate
    ? R.prop('total', totals)
    : R.prop('dueToPay', invoice)

  return includeServiceFee ? totalAmountFee : totalAmountNoFee
}

export const calculateDiscountAmount = (
  isEstimate: boolean,
  isChewyCheckoutEnabledAndHasRetailOrder: boolean,
  invoice: InvoiceOrEstimate | BatchInvoice,
  grandTotal: Totals | undefined,
  totals: InvoiceTotals,
) => {
  if (isEstimate) {
    return R.prop('totalDiscountWithAdditional', totals)
  }

  if (isChewyCheckoutEnabledAndHasRetailOrder) {
    return grandTotal?.discount
  }

  return (
    R.prop('additionalDiscount', invoice) +
    (R.prop('totalDiscount', invoice) || 0)
  )
}

export const getClientBalanceData = (
  invoice: InvoiceOrEstimate | BatchInvoice,
  clientBillingActivityBalance: number,
) => {
  const clientBalanceDate = invoice.paid
    ? invoice.paidDate
    : moment().toDate().toISOString()

  const clientBalance = invoice.paid
    ? invoice.balanceAfterPaid
    : clientBillingActivityBalance

  return { clientBalanceDate, clientBalance }
}

export const calculateOverageAmount = (
  completedPayments: Array<GraphQlPayment | RetailPaymentDetail | Payment>,
  isChewyPaymentFn: (
    payment: GraphQlPayment | RetailPaymentDetail | Payment,
  ) => payment is RetailPaymentDetail,
) =>
  completedPayments.reduce((totalOverage, currentPayment) => {
    if (isChewyPaymentFn(currentPayment)) {
      return totalOverage
    }

    return currentPayment.split &&
      currentPayment.splitAmount &&
      currentPayment.amount
      ? totalOverage + (currentPayment.amount - currentPayment.splitAmount)
      : totalOverage
  }, 0)

export const getSubtotalValue = (
  isEstimate: boolean,
  isChewyCheckoutEnabledAndHasRetailOrder: boolean,
  invoice: InvoiceOrEstimate | BatchInvoice,
  grandTotal: Totals | undefined,
  totals: InvoiceTotals,
) => {
  if (isEstimate) {
    return totals.totalWithoutTax
  }

  if (isChewyCheckoutEnabledAndHasRetailOrder) {
    return grandTotal?.subTotal
  }

  return invoice.subtotal
}

export const getTaxValue = (
  isEstimate: boolean,
  isChewyCheckoutEnabledAndHasRetailOrder: boolean,
  invoice: InvoiceOrEstimate | BatchInvoice,
  grandTotal: Totals | undefined,
  totals: InvoiceTotals,
) => {
  if (isEstimate) {
    return totals.totalTax
  }

  if (isChewyCheckoutEnabledAndHasRetailOrder) {
    return grandTotal?.tax
  }

  return R.prop('totalTax', invoice)
}

export const getTitleValue = (
  isEstimate: boolean,
  isChewyCheckoutEnabledAndHasRetailOrder: boolean,
  invoice: InvoiceOrEstimate | BatchInvoice,
  grandTotal: Totals | undefined,
  totals: InvoiceTotals,
) => {
  if (isEstimate) {
    return totals.totalWithoutFee
  }

  if (isChewyCheckoutEnabledAndHasRetailOrder) {
    return grandTotal?.total
  }

  return invoice.amountNoFee
}

export const getCollapsibleTitle = (
  isBatchInvoice: boolean,
  isChewyCheckoutEnabled: boolean,
  isEstimate: boolean,
  invoicesLength: number | undefined,
  t: (key: string, options?: any) => string,
) =>
  isBatchInvoice
    ? t('Invoices:GRAND_TOTAL_INVOICES', { invoicesLength })
    : isChewyCheckoutEnabled && !isEstimate
      ? t('Common:GRAND_TOTAL')
      : t('Common:INVOICE_TOTAL')

export const getShippingLabel = (
  isChewyFreeShippingEnabled: boolean,
  grandTotalShipping: number | undefined,
  isFreeShippingEligible: boolean,
  t: (key: string) => string,
) =>
  isChewyFreeShippingEnabled &&
  grandTotalShipping === 0 &&
  isFreeShippingEligible
    ? t('Common:FREE_SHIPPING')
    : t('Common:SHIPPING')

export const getInvoiceTotalDueLabel = (
  isChewyCheckoutEnabled: boolean,
  isEstimate: boolean,
  t: (key: string) => string,
) => {
  if (isChewyCheckoutEnabled && !isEstimate) {
    return t('Invoices:CVC_INVOICE_TOTAL_DUE')
  }

  return t('Invoices:INVOICE_TOTAL_DUE')
}
